import { createAsyncThunk, createSlice, type Dispatch, type PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../Models/ApiModel';
import type { LessonDraftModel, LessonModel } from '../Models/LessonModel';
import type { LessonPatch } from '../Services/LessonService';
import type { LessonStatus } from '../Models/enums';
import { LessonService } from '../Services/LessonService';
import { CalendarAutoSync } from '../Services/CalendarAutoSync';
import type { RootState } from './store';
import { removeStudent, type LoadState } from './studentsSlice';

export interface LessonsState {
  items: LessonModel[];
  state: LoadState;
  error: string | null;
}

const initialState: LessonsState = { items: [], state: 'idle', error: null };

function message(error: unknown): string {
  return error instanceof ApiError ? error.code : 'SERVER_ERROR';
}

export const loadLessons = createAsyncThunk('lessons/load', async () => LessonService.list());

export const createLesson = createAsyncThunk(
  'lessons/create',
  async (draft: LessonDraftModel, { dispatch, getState }) => {
    const lesson = await LessonService.create(draft);
    pushToCalendar(lesson, getState() as RootState, dispatch);
    return lesson;
  }
);

/**
 * A full edit of a lesson: who it was with, when, and for how much.
 *
 * Changing the date can move the lesson past other lessons in the list, so the
 * reducer re-sorts rather than replacing in place.
 */
export const editLesson = createAsyncThunk(
  'lessons/edit',
  async (input: { id: number; patch: LessonPatch }, { dispatch, getState }) => {
    const lesson = await LessonService.update(input.id, input.patch);
    pushToCalendar(lesson, getState() as RootState, dispatch);
    return lesson;
  }
);

/**
 * Marking a lesson paid, unpaid or cancelled. A cancellation is the one that
 * matters to the calendar: the lesson is no longer an appointment, so the
 * event goes, and an undo brings it back.
 */
export const setLessonStatus = createAsyncThunk(
  'lessons/setStatus',
  async (input: { id: number; status: LessonStatus }, { dispatch, getState }) => {
    const lesson = await LessonService.setStatus(input.id, input.status);
    pushToCalendar(lesson, getState() as RootState, dispatch);
    return lesson;
  }
);

export const postponeLesson = createAsyncThunk(
  'lessons/postpone',
  async (input: { id: number; date: string; time: string | null }, { dispatch, getState }) => {
    const lesson = await LessonService.postpone(input.id, input.date, input.time);
    // The move is the whole point of a postponement, so this is the one the
    // calendar most needs to hear about.
    pushToCalendar(lesson, getState() as RootState, dispatch);
    return lesson;
  }
);

/** Wipes the whole ledger. Guarded by a confirmation at every call site. */
export const clearLessons = createAsyncThunk('lessons/clear', async (_: void, { getState }) => {
  // Read before the delete, or there is nothing left to read the event ids from.
  const doomed = (getState() as RootState).lessons.items.map(lesson => ({ gcalEventId: lesson.gcalEventId }));
  await LessonService.removeAll();
  void CalendarAutoSync.lessonsRemoved(doomed);
});

export const removeLesson = createAsyncThunk('lessons/remove', async (id: number, { getState }) => {
  const lesson = (getState() as RootState).lessons.items.find(item => item.id === id);
  await LessonService.remove(id);
  if (lesson) void CalendarAutoSync.lessonRemoved(lesson);
  return id;
});

function replace(items: LessonModel[], lesson: LessonModel): LessonModel[] {
  const index = items.findIndex(item => item.id === lesson.id);
  if (index === -1) return [...items, lesson];
  const next = items.slice();
  next[index] = lesson;
  return next;
}

/** The list is kept in the order the API serves it: by date, then by id. */
function sorted(items: LessonModel[]): LessonModel[] {
  return items.slice().sort((a, b) => (a.date === b.date ? a.id - b.id : a.date.localeCompare(b.date)));
}

/**
 * Sends a saved lesson to Google Calendar without making the save wait for it.
 *
 * Deliberately not awaited: the lesson is already stored, and a calendar that
 * is a second behind beats a save that fails because a third party was slow.
 * When the push creates a new event its id is folded back into the store so
 * the next edit patches that event instead of creating a second one, and
 * when a cancellation removes the event the link is cleared the same way.
 */
function pushToCalendar(lesson: LessonModel, state: RootState, dispatch: Dispatch): void {
  const userId = state.session.user?.uid ?? '';
  void CalendarAutoSync.lessonSaved(lesson, userId).then(change => {
    if (change) dispatch(lessonLinked({ id: lesson.id, gcalEventId: change.gcalEventId }));
  });
}

const lessonsSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    /** Records the calendar event a lesson was just pushed to, or that it has none. */
    lessonLinked(state, action: PayloadAction<{ id: number; gcalEventId: string | null }>) {
      const lesson = state.items.find(item => item.id === action.payload.id);
      if (lesson) lesson.gcalEventId = action.payload.gcalEventId;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadLessons.pending, state => {
        state.state = 'loading';
        state.error = null;
      })
      .addCase(loadLessons.fulfilled, (state, action) => {
        state.items = action.payload;
        state.state = 'ready';
      })
      .addCase(loadLessons.rejected, (state, action) => {
        state.state = 'failed';
        state.error = message(action.error);
      })
      .addCase(createLesson.fulfilled, (state, action) => {
        state.items = sorted([...state.items, action.payload]);
      })
      .addCase(setLessonStatus.fulfilled, (state, action) => {
        state.items = replace(state.items, action.payload);
      })
      .addCase(editLesson.fulfilled, (state, action) => {
        state.items = sorted(replace(state.items, action.payload));
      })
      .addCase(postponeLesson.fulfilled, (state, action) => {
        // A postponed lesson has moved date, so the list has to be reordered
        // rather than updated in place.
        state.items = sorted(replace(state.items, action.payload));
      })
      .addCase(removeLesson.fulfilled, (state, action) => {
        state.items = state.items.filter(lesson => lesson.id !== action.payload);
      })
      .addCase(clearLessons.fulfilled, state => {
        state.items = [];
      })
      // The API cascades a deleted student's lessons away; the store has to
      // follow, or the calendar keeps drawing them until the next reload.
      .addCase(removeStudent.fulfilled, (state, action) => {
        state.items = state.items.filter(lesson => lesson.student !== action.payload);
      });
  }
});

export const { lessonLinked } = lessonsSlice.actions;

export default lessonsSlice.reducer;
